import { useMemo } from 'react'

import { alpha } from '@mui/material/styles'
import { ChartData, ChartOptions } from 'chart.js'
import { format } from 'date-fns'
import { Chart } from 'react-chartjs-2'

import {
  COLOR_FULFILMENT_RATE,
  DRIVER_APP_FULFILMENT_CHART_HEIGHT,
  FULFILMENT_TREND_DAILY_POINT_ALPHA,
  FULFILMENT_TREND_DAILY_POINT_RADIUS,
  FULFILMENT_TREND_LABEL_PADDING_TOP,
  FULFILMENT_TREND_MAX_POINT_LABELS,
} from 'components/reports/Metrics/constants/DriverAppFulfilment'
import { DATE_DAY_MONTH_SHORT_FORMAT } from 'constants/DateFormats'
import { DriverAppFulfilmentTrendPointDto } from 'generated/graphql'
import { formatRate, fulfilmentTrendChartPlugins } from 'helpers/fulfilmentTrendChart.helpers'

import { LINE_STYLE } from '../components/chartConfig'

interface Props {
  points: DriverAppFulfilmentTrendPointDto[]
  height?: number
}

export const FulfilmentTrendChart = ({
  points,
  height = DRIVER_APP_FULFILMENT_CHART_HEIGHT,
}: Props) => {
  const data = useMemo<ChartData<'line', number[], string>>(
    () => ({
      labels: points.map((point) => format(new Date(point.date), DATE_DAY_MONTH_SHORT_FORMAT)),
      datasets: [
        // Dataset 0 draws on top and carries the value labels and the tooltip. Clipping is off so
        // markers sitting exactly on the 100% ceiling render whole instead of being cut in half.
        {
          ...LINE_STYLE,
          type: 'line' as const,
          clip: false as const,
          label: '% fulfilled through Driver App (7-day rolling average)',
          data: points.map((point) => Number(point.rollingFulfilmentRate)),
          borderColor: COLOR_FULFILMENT_RATE,
          backgroundColor: alpha(COLOR_FULFILMENT_RATE, 0.12),
          pointBackgroundColor: COLOR_FULFILMENT_RATE,
          fill: true,
        },
        // The raw daily rate underneath, as unconnected faded points.
        {
          type: 'line' as const,
          clip: false as const,
          label: 'Daily % fulfilled through Driver App',
          data: points.map((point) => Number(point.dailyFulfilmentRate)),
          showLine: false,
          pointRadius: FULFILMENT_TREND_DAILY_POINT_RADIUS,
          pointHoverRadius: FULFILMENT_TREND_DAILY_POINT_RADIUS,
          borderColor: alpha(COLOR_FULFILMENT_RATE, FULFILMENT_TREND_DAILY_POINT_ALPHA),
          backgroundColor: alpha(COLOR_FULFILMENT_RATE, FULFILMENT_TREND_DAILY_POINT_ALPHA),
          pointBackgroundColor: alpha(COLOR_FULFILMENT_RATE, FULFILMENT_TREND_DAILY_POINT_ALPHA),
        },
      ],
    }),
    [points],
  )

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: { padding: { top: FULFILMENT_TREND_LABEL_PADDING_TOP } },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: FULFILMENT_TREND_MAX_POINT_LABELS,
          },
        },
        y: {
          beginAtZero: true,
          max: 100,
          ticks: { stepSize: 20, callback: (value) => `${value}%` },
        },
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: { usePointStyle: true, pointStyle: 'line', boxWidth: 24 },
        },
        tooltip: {
          callbacks: {
            // One block per day: the rolling dataset carries the whole tooltip, the daily-points
            // dataset adds nothing of its own.
            label: (context) => {
              if (context.datasetIndex !== 0) {
                return undefined
              }

              const point = points[context.dataIndex]

              return [
                `7-day rate: ${formatRate(Number(point.rollingFulfilmentRate))}`,
                `On the day: ${formatRate(Number(point.dailyFulfilmentRate))}`,
                `Driver App: ${point.driverAppFulfilledJobs.toLocaleString()}`,
                `CX: ${point.cxFulfilledJobs.toLocaleString()}`,
                `Posted: ${point.totalJobsPosted.toLocaleString()}`,
              ]
            },
          },
        },
      },
    }),
    [points],
  )

  return (
    <div style={{ height, position: 'relative' }}>
      <Chart<'line', number[], string>
        type='line'
        data={data}
        options={options}
        plugins={fulfilmentTrendChartPlugins}
      />
    </div>
  )
}
